import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@ngneat/query';
import { DataQueryServiceConfiguration } from './data-query-service-configuration';
import {
    catchError,
    concatMap,
    EMPTY,
    expand,
    filter,
    from,
    map,
    Observable,
    of,
    OperatorFunction,
    throwError,
    toArray,
} from 'rxjs';
import { PagedResultWithPointers } from '../../models/paging/paged-result';
import {
    NotificationRef,
    NotificationService,
} from '@progress/kendo-angular-notification';

@Injectable({
    providedIn: 'root',
})
export class DataQueryService {
    protected http = inject(HttpClient);
    private notificationService = inject(NotificationService);

    protected readonly mutation = injectMutation();
    protected readonly query = injectQuery();
    private readonly queryClient = injectQueryClient();

    private activeNotification?: NotificationRef;

    prefetch(): Observable<any> {
        return of(void 0);
    }

    get<
        T,
        Y extends
            | {
                  [param: string]:
                      | string
                      | number
                      | boolean
                      | readonly (string | number | boolean)[];
              }
            | undefined = undefined,
    >(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        parameters?: Y,
    ) {
        return this.query({
            queryKey: [dataServiceConfiguration.key, parameters] as const,
            queryFn: () =>
                this.http
                    .get<T>(dataServiceConfiguration.url, {
                        params: new HttpParams({ fromObject: parameters }),
                    })
                    .pipe(catchError((error) => this.handleError(error))),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
            refetchOnWindowFocus: false,
        });
    }

    protected prefetchGet<
        T,
        Y extends
            | {
                  [param: string]:
                      | string
                      | number
                      | boolean
                      | readonly (string | number | boolean)[];
              }
            | undefined = undefined,
    >(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        parameters?: Y,
    ) {
        return from(
            this.queryClient.prefetchQuery({
                queryKey: [dataServiceConfiguration.key, parameters] as const,
                queryFn: () =>
                    this.http
                        .get<T>(dataServiceConfiguration.url, {
                            params: new HttpParams({ fromObject: parameters }),
                        })
                        .pipe(catchError((error) => this.handleError(error))),
                throwOnError: false,
                staleTime: dataServiceConfiguration.staleTime,
                retry: false,
            }),
        );
    }

    getWithTransform<
        T,
        Y,
        Z extends
            | {
                  [param: string]:
                      | string
                      | number
                      | boolean
                      | readonly (string | number | boolean)[];
              }
            | undefined = undefined,
    >(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        transform: OperatorFunction<T, Y>,
        transformedKey: string,
        parameters?: Z,
    ) {
        return this.query({
            queryKey: [
                dataServiceConfiguration.key + '-' + transformedKey,
                parameters,
            ] as const,
            queryFn: () =>
                this.get(dataServiceConfiguration, parameters).result$.pipe(
                    filter((results) => !!results.data),
                    map((results) => results.data!),
                    transform,
                ),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
            refetchOnWindowFocus: false,
        });
    }

    protected prefetchGetWithTransform<
        T,
        Y,
        Z extends
            | {
                  [param: string]:
                      | string
                      | number
                      | boolean
                      | readonly (string | number | boolean)[];
              }
            | undefined = undefined,
    >(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        transform: OperatorFunction<T, Y>,
        parameters?: Z,
    ) {
        return from(
            this.queryClient.prefetchQuery({
                queryKey: [dataServiceConfiguration.key, parameters] as const,
                queryFn: () =>
                    this.http
                        .get<T>(dataServiceConfiguration.url, {
                            params: new HttpParams({ fromObject: parameters }),
                        })
                        .pipe(catchError((error) => this.handleError(error)))
                        .pipe(transform),
                throwOnError: false,
                staleTime: dataServiceConfiguration.staleTime,
                retry: false,
            }),
        );
    }

    post<T, Y = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        body?: Y,
    ) {
        return this.query({
            queryKey: [dataServiceConfiguration.key, body] as const,
            queryFn: () =>
                this.http
                    .post<T>(dataServiceConfiguration.url, body, {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                        }),
                    })
                    .pipe(catchError((error) => this.handleError(error))),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
            refetchOnWindowFocus: false,
        });
    }

    postWithoutCache<T, Y = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        body?: Y,
    ): Observable<T> {
        return this.http
            .post<T>(dataServiceConfiguration.url, body, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                }),
            })
            .pipe(catchError((error) => this.handleError(error)));
    }

    prefetchPost<T, Y = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        body?: Y,
    ) {
        return from(
            this.queryClient.prefetchQuery({
                queryKey: [dataServiceConfiguration.key, body] as const,
                queryFn: () =>
                    this.http
                        .post<T>(dataServiceConfiguration.url, body, {
                            headers: new HttpHeaders({
                                'Content-Type': 'application/json',
                            }),
                        })
                        .pipe(catchError((error) => this.handleError(error))),
                throwOnError: false,
                staleTime: dataServiceConfiguration.staleTime,
                retry: false,
            }),
        );
    }

    postWithTransform<T, Y = T, Z = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        transform?: OperatorFunction<T, Y>,
        body?: Z,
    ) {
        return this.query({
            queryKey: [dataServiceConfiguration.key, body] as const,
            queryFn: () =>
                this.http
                    .post<T>(dataServiceConfiguration.url, body, {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                        }),
                    })
                    .pipe(catchError((error) => this.handleError(error)))
                    .pipe(
                        transform ??
                            ((source: Observable<T>) =>
                                source as unknown as Observable<Y>),
                    ),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
        });
    }

    postWithTransformUnique<T, Y, Z = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        uniqueSuffix: string,
        transform?: OperatorFunction<T, Y>,
        body?: Z,
    ) {
        return this.query({
            queryKey: [
                dataServiceConfiguration.key + '-' + uniqueSuffix,
                body,
            ] as const,
            queryFn: () =>
                this.http
                    .post<T>(dataServiceConfiguration.url, body, {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                        }),
                    })
                    .pipe(catchError((error) => this.handleError(error)))
                    .pipe(
                        transform ??
                            ((source: Observable<T>) =>
                                source as unknown as Observable<Y>),
                    ),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
        });
    }

    postWithTransformPaged<T, Y, Z = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        transform?: OperatorFunction<T[], Y>,
        body?: Z,
    ) {
        return this.query({
            queryKey: [dataServiceConfiguration.key, 'all', body] as const,
            queryFn: () =>
                this.postAllPages(dataServiceConfiguration, body).pipe(
                    catchError((error) => this.handleError(error)),
                    transform ??
                        ((source: Observable<T[]>) =>
                            source as unknown as Observable<Y>),
                ),
            throwOnError: false,
            staleTime: dataServiceConfiguration.staleTime,
            retry: false,
        });
    }

    private executePagedPost<T, Y>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        body: Y,
    ) {
        return this.http
            .post<PagedResultWithPointers<T, Y>>(
                dataServiceConfiguration.url,
                body,
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                    }),
                },
            )
            .pipe(catchError((error) => this.handleError(error)));
    }

    private postAllPages<T, U>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        body?: U,
    ): Observable<T[]> {
        return this.executePagedPost(dataServiceConfiguration, body).pipe(
            expand(({ nextPage }) =>
                nextPage
                    ? this.executePagedPost(dataServiceConfiguration, nextPage)
                    : EMPTY,
            ),
            concatMap(({ results }) => results),
            toArray(),
        );
    }

    prefetchPostWithTransform<T, Y, Z = undefined>(
        dataServiceConfiguration: DataQueryServiceConfiguration<T>,
        transform: OperatorFunction<T, Y>,
        body?: Z,
    ) {
        return from(
            this.queryClient.prefetchQuery({
                queryKey: [dataServiceConfiguration.key, body] as const,
                queryFn: () =>
                    this.http
                        .post<T>(dataServiceConfiguration.url, body, {
                            headers: new HttpHeaders({
                                'Content-Type': 'application/json',
                            }),
                        })
                        .pipe(catchError((error) => this.handleError(error)))
                        .pipe(transform),
                throwOnError: false,
                staleTime: dataServiceConfiguration.staleTime,
                retry: false,
            }),
        );
    }

    refetchQuery(key: string, bodyOrParameters: any) {
        return from(
            this.queryClient.refetchQueries({
                predicate: (x) =>
                    x.queryKey == ([key, bodyOrParameters] as const),
            }),
        );
    }

    refetchAnyQuery(key: string) {
        return from(
            this.queryClient.refetchQueries({
                predicate: (x) => x.queryKey[0] == key,
            }),
        );
    }

    protected handleError(error: HttpErrorResponse) {
        if (this.activeNotification) {
            this.activeNotification.hide();
        }

        if (error.headers.has('BadRequestMessage')) {
            this.activeNotification = this.notificationService.show({
                content: error.headers.get('BadRequestMessage')!,
                type: { style: 'error', icon: true },
                hideAfter: 5000,
                cssClass: ['validation-message-notification'],
            });
        } else {
            this.activeNotification = this.notificationService.show({
                content: 'An error occurred. Please try again.',
                type: { style: 'error', icon: true },
                hideAfter: 5000,
                cssClass: ['validation-message-notification'],
            });
        }

        return throwError(() => error);
    }
}
